/**
 * Transforms a GoodsShipment GraphQL object to/from the form state.
 *
 * IMPORTANT: toSchema() must only output fields valid on the mutation input.
 * Do NOT spread the original — it contains federated/queried fields (like
 * importer.name, __typename) that the mutation rejects.
 *
 * @see LLD Section 3.2 — Transform Classes
 */
import { BaseTransformer } from './BaseTransformer';
import { GoodsShipmentFormState } from '../schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from './GoodsItemDetailTransformer';
import { getEditedGoodsItemsForShipment } from './goodsItemFormRef';
import { stripTypeNames } from '@xbcb/js-utils/dist';
import type {
  goodsShipmentFieldsFragment,
  GoodsShipmentInput,
  TransportContractDocumentType,
  BorderTransportMeansInput,
  TransportEquipmentInput,
} from '__generated__/graphql';

/** Use the codegen fragment type as the API data shape */
export type GoodsShipmentApiData = goodsShipmentFieldsFragment;

/*
 * Stamps shipment-level fields onto an individual item at save time.
 * Currency is always stamped (shipment currency is authoritative for all items).
 * Extensible: add new stamp logic here as shipment→item fields are identified.
 */
const stampShipmentFields = (
  item: Record<string, any>,
  formData: GoodsShipmentFormState,
): Record<string, any> => {
  const currency = formData.currency;
  if (!currency) return item;

  // Stamp currency onto customsValue (always — shipment currency is authoritative)
  if (item.customsValue?.value != null) {
    item.customsValue = { ...item.customsValue, currency };
  }

  // Stamp currency onto each valuationAdjustment amount
  if (item.valuationAdjustment) {
    item.valuationAdjustment = item.valuationAdjustment.map((a: any) =>
      a.amount ? { ...a, amount: { ...a.amount, currency } } : a,
    );
  }

  return item;
};

export class CuDeGoodsShipmentTransformer extends BaseTransformer<
  GoodsShipmentApiData,
  GoodsShipmentFormState,
  GoodsShipmentInput
> {
  toForm(source: GoodsShipmentApiData): GoodsShipmentFormState {
    const consignment = source.consignment?.[0];
    const doc = consignment?.transportContractDocument?.[0];
    const tradeTerms = source.tradeTerms?.[0];
    const supportingDocs = source.supportingDocument ?? [];
    const previousDocs = source.previousDocument ?? [];

    return {
      transportDoc: {
        type: doc?.type ?? null,
        identification: doc?.identification ?? null,
      },
      supportingDocs:
        supportingDocs.length > 0
          ? supportingDocs.map((d) => ({
              type: d.type ?? null,
              identification: d.identification ?? null,
            }))
          : [{ type: null, identification: null }],
      previousDocs:
        previousDocs.length > 0
          ? previousDocs.map((d) => ({
              type: d.type ?? null,
              identification: d.identification ?? null,
              category: d.category ?? null,
              line: d.line ?? null,
            }))
          : [{ type: null, identification: null, category: null, line: null }],
      incoterms: tradeTerms?.condition ?? null,
      incotermPlace: tradeTerms?.location ?? null,
      currency: source.goodsItem?.[0]?.customsValue?.currency ?? null,
      invoiceValue: consignment?.value ?? null,
      exportCountry: source.exportCountry?.[0]?.country ?? null,
      importCountry: source.importCountry?.[0]?.country ?? null,
      adjustments: (source.valuationAdjustment ?? []).map((a) => ({
        type: a.addition ?? null,
        amount: a.amount?.value ?? null,
        currency: a.amount?.currency ?? null,
        percentage: a.percentage ?? null,
      })),
      importer: source.importer?.[0]
        ? { id: source.importer[0].id, name: source.importer[0].name ?? null }
        : null,
      buyer: source.buyer?.[0]
        ? { id: source.buyer[0].id, name: source.buyer[0].name ?? null }
        : null,
      consignee: source.consignee?.[0]
        ? { id: source.consignee[0].id, name: source.consignee[0].name ?? null }
        : null,
    };
  }

  toSchema(
    formData: GoodsShipmentFormState,
    original: GoodsShipmentApiData,
    shipmentIndex = 0,
  ): GoodsShipmentInput {
    const originalConsignment = original.consignment?.[0];

    // Filter out empty supporting docs (both fields null/empty)
    const supportingDocument = formData.supportingDocs
      .filter((d) => d.type || d.identification)
      .map((d) => ({
        type: d.type,
        identification: d.identification,
      }));

    // Filter out empty previous docs (all fields null/empty)
    const previousDocument = formData.previousDocs
      .filter((d) => d.type || d.identification || d.category || d.line)
      .map((d) => ({
        type: d.type,
        identification: d.identification,
        category: d.category,
        line: d.line,
      }));

    return {
      tradeTerms: [
        {
          condition: formData.incoterms,
          location: formData.incotermPlace,
        },
      ],
      supportingDocument:
        supportingDocument.length > 0 ? supportingDocument : undefined,
      previousDocument:
        previousDocument.length > 0 ? previousDocument : undefined,
      // TODO: When apportioning is implemented, distribute these shipment-level
      // adjustments proportionally among goodsItem[].valuationAdjustment on save.
      valuationAdjustment: formData.adjustments
        .filter((a) => a.type)
        .map((a) => ({
          addition: a.type,
          amount:
            a.amount != null
              ? { value: a.amount, currency: a.currency ?? formData.currency }
              : null,
          percentage: a.percentage,
        })),
      exportCountry: [{ country: formData.exportCountry }],
      importCountry: [{ country: formData.importCountry }],
      consignment: [
        {
          value: formData.invoiceValue,
          transportContractDocument: [
            {
              type: formData.transportDoc.type as TransportContractDocumentType,
              identification: formData.transportDoc.identification,
            },
          ],
          // Preserve non-editable fields managed by Transportation tab.
          borderTransportationMean:
            originalConsignment?.borderTransportationMean
              ? (stripTypeNames(
                  originalConsignment.borderTransportationMean,
                ) as unknown as BorderTransportMeansInput[])
              : undefined,
          transportEquipment: originalConsignment?.transportEquipment
            ? (stripTypeNames(
                originalConsignment.transportEquipment,
              ) as unknown as TransportEquipmentInput[])
            : undefined,
        },
      ],
      importer: formData.importer ? [{ id: formData.importer.id }] : undefined,
      buyer: formData.buyer ? [{ id: formData.buyer.id }] : undefined,
      consignee: formData.consignee
        ? [{ id: formData.consignee.id }]
        : undefined,
      goodsItem: (() => {
        const edited = getEditedGoodsItemsForShipment(shipmentIndex);
        const itemTransformer = new GoodsItemDetailTransformer();
        return original.goodsItem?.map((item) => {
          let result;
          const entry = edited.get(item.sequence!);
          if (entry) {
            result = {
              ...itemTransformer.toSchema(entry.formData, entry.original),
            };
          } else {
            // Unvisited: round-trip through transformer to emit only mutation-valid fields
            result = {
              ...itemTransformer.toSchema(itemTransformer.toForm(item), item),
            };
          }
          return stampShipmentFields(result, formData);
        });
      })(),
    };
  }
}
